/**
 * Cayman Islands timezone (EST, UTC-5, no DST).
 * Use these helpers for all date display/comparison so admin users
 * always see dates as they are in Cayman regardless of browser TZ.
 *
 * Calendar-date convention:
 *   Invoice / quote / receipt / payment / due dates are *calendar dates*
 *   (the user picked a day in a <input type="date"> — the time component
 *   is meaningless). When the backend returns these as naive ISO strings
 *   like "2026-02-22T00:00:00", we render the "2026-02-22" part directly
 *   without any TZ conversion. This prevents "day forward / day backward"
 *   drift that occurs when `new Date("2026-02-22T00:00:00Z")` is shifted
 *   into a different timezone for display.
 *
 * Timestamp convention:
 *   Fields like `created_at` / `updated_at` are real UTC moments. When
 *   serialized with a `Z` or explicit offset (e.g. "2026-02-22T23:59:00Z")
 *   they are converted to Cayman wall-clock time.
 */
export const CAYMAN_TZ = 'America/Cayman';

// YYYY-MM-DD prefix (calendar-date portion of an ISO string).
const DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/;
// Does an ISO string carry an explicit timezone (Z or ±HH:MM)?
const TZ_SUFFIX_RE = /(Z|[+-]\d{2}:?\d{2})$/;

/**
 * Treat a value as a calendar-date string when possible.
 * Returns "YYYY-MM-DD" or null.
 *
 *   "2026-02-22"                    → "2026-02-22"  (plain date)
 *   "2026-02-22T00:00:00"           → "2026-02-22"  (naive ISO — calendar)
 *   "2026-02-22T00:00:00.000"       → "2026-02-22"  (naive ISO — calendar)
 *   "2026-02-22T00:00:00Z"          → null          (real UTC timestamp)
 *   "2026-02-22T00:00:00+00:00"     → null          (real zoned timestamp)
 *    Date object / number           → null          (real timestamp)
 */
const asCalendarDate = (dateInput) => {
  if (typeof dateInput !== 'string') return null;
  const m = dateInput.match(DATE_PREFIX_RE);
  if (!m) return null;
  if (TZ_SUFFIX_RE.test(dateInput)) return null; // carries an explicit tz → real instant
  return `${m[1]}-${m[2]}-${m[3]}`;
};

/**
 * Returns today's date in Cayman timezone as "YYYY-MM-DD".
 * Use this for pre-filling <input type="date"> so you never show
 * "tomorrow's UTC date" to a user who's in Cayman after 7pm.
 */
export const caymanToday = () => caymanDateKey(new Date());

/**
 * Get YYYY-MM-DD key for a date.
 *   - Calendar-date inputs return their own day, as-is.
 *   - Real timestamps (Date, number, or ISO with tz) are converted to
 *     the Cayman calendar date.
 */
export const caymanDateKey = (dateInput) => {
  if (!dateInput) return '';
  const cal = asCalendarDate(dateInput);
  if (cal) return cal;
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  // en-CA produces YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CAYMAN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
};

const FULL_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const renderCalendar = (cal, months) => {
  const [, y, mm, dd] = cal.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return `${dd} ${months[parseInt(mm, 10) - 1]} ${y}`;
};

/**
 * Format a date to "03 April 2026".
 *   - Calendar-date input  → rendered directly (no TZ shift).
 *   - Zoned timestamp      → converted to Cayman wall-clock.
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const cal = asCalendarDate(dateString);
  if (cal) return renderCalendar(cal, FULL_MONTHS);

  const date = new Date(dateString);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CAYMAN_TZ,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).formatToParts(date);
  const day = parts.find(p => p.type === 'day').value;
  const month = parts.find(p => p.type === 'month').value;
  const year = parts.find(p => p.type === 'year').value;
  return `${day} ${month} ${year}`;
};

/**
 * Format a date to "03 Apr 2026".
 * Same semantics as formatDate.
 */
export const formatDateShort = (dateString) => {
  if (!dateString) return '';
  const cal = asCalendarDate(dateString);
  if (cal) return renderCalendar(cal, SHORT_MONTHS);

  const date = new Date(dateString);
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: CAYMAN_TZ,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).formatToParts(date);
  const day = parts.find(p => p.type === 'day').value;
  const month = parts.find(p => p.type === 'month').value;
  const year = parts.find(p => p.type === 'year').value;
  return `${day} ${month} ${year}`;
};
